/**
 * The shop's URL table — ONE list, read by both halves.
 *
 * The client router (`web/src/store/route.tsx` → `readRoute`) and the Worker's front door
 * (`shop-seo.ts` → `serveShopFrontDoor`) both match a path inside the shop against this table.
 * Before it existed they were two hand-kept lists, and they had drifted: `/help`, `/collections`
 * and `/signup` were real pages in the browser and 404 + noindex on the server, so a refresh or a
 * crawler on any of them saw "Page not found". A path is a shop page if and only if it matches
 * here; anything else 404s on the server BEFORE a database is opened, and renders the not-found
 * page in the browser.
 *
 * ⚠️ No DOM, no React, no Worker types — this file is imported by the Vite bundle and by the Worker.
 * Params are returned RAW (still percent-encoded); each side decodes, and a malformed escape is a
 * not-found, never a throw.
 */

export type ShopRouteName =
  | "home"
  | "category"
  | "dept"
  | "search"
  | "clearance"
  | "campaign"
  | "bundles"
  | "guides"
  | "collections"
  | "help"
  | "faq"
  | "contact"
  | "about"
  | "product"
  | "basket"
  | "checkout"
  | "account"
  | "signup"
  | "verify"
  | "reset"
  | "welcome"
  | "order"
  | "orders"
  | "track"
  | "review"
  | "policy";

export interface ShopRouteDef {
  name: ShopRouteName;
  /** Matched against the path inside the shop, leading slash, no trailing slash ("/" for home). */
  re: RegExp;
  /**
   * `index` = a page with public content a search engine may list (on an indexable host).
   * `private` = a customer's own flow (basket, checkout, account…) — always noindex.
   */
  crawl: "index" | "noindex" | "private";
  /** A concrete path for checks (`verify:shop-nav` renders every one of these). */
  sample: string;
  /** Title for the generic server-rendered shell, for pages with no data of their own. */
  title?: string;
}

export const SHOP_ROUTES: readonly ShopRouteDef[] = [
  { name: "home", re: /^\/$/, crawl: "index", sample: "/" },
  { name: "category", re: /^\/c\/([^/]+)$/, crawl: "index", sample: "/c/kitchen-stuff" },
  { name: "dept", re: /^\/d\/([^/]+)$/, crawl: "index", sample: "/d/2-tools" },
  { name: "search", re: /^\/search$/, crawl: "noindex", sample: "/search" },
  { name: "clearance", re: /^\/clearance$/, crawl: "index", sample: "/clearance", title: "Clearance" },
  /**
   * Holiday sale pages (shop-campaigns.ts): `/s/<slug>` is one campaign (Black Friday, Eid…),
   * `/s` lists the ones on now or coming up. A draft or ended campaign is a 404.
   */
  { name: "campaign", re: /^\/s(?:\/([^/]+))?$/, crawl: "index", sample: "/s", title: "Sales" },
  /**
   * Bundles & Kits (shop-bundles.ts): `/bundles` lists them by theme, `/bundles/<slug>` is one
   * bundle. One entry, optional parameter — the list is the sample every check renders.
   */
  { name: "bundles", re: /^\/bundles(?:\/([^/]+))?$/, crawl: "index", sample: "/bundles", title: "Bundles & Kits" },
  /** Buying guides (shop-guides.ts): `/guides` lists the published ones, `/guides/<slug>` is one. A draft is a 404. */
  { name: "guides", re: /^\/guides(?:\/([^/]+))?$/, crawl: "index", sample: "/guides", title: "Buying guides" },
  { name: "collections", re: /^\/collections$/, crawl: "index", sample: "/collections", title: "All collections" },
  /** `/help` is the old URL of the FAQ: it renders the same page, canonical /faq (shop-seo.ts). */
  { name: "help", re: /^\/help$/, crawl: "index", sample: "/help", title: "Help" },
  { name: "faq", re: /^\/faq$/, crawl: "index", sample: "/faq", title: "FAQ" },
  { name: "contact", re: /^\/contact$/, crawl: "index", sample: "/contact", title: "Contact us" },
  { name: "about", re: /^\/about$/, crawl: "index", sample: "/about", title: "About us" },
  { name: "product", re: /^\/p\/([^/]+)$/, crawl: "index", sample: "/p/p-1" },
  { name: "basket", re: /^\/basket$/, crawl: "private", sample: "/basket", title: "Cart" },
  { name: "checkout", re: /^\/checkout$/, crawl: "private", sample: "/checkout", title: "Checkout" },
  { name: "account", re: /^\/account$/, crawl: "private", sample: "/account", title: "Your account" },
  { name: "signup", re: /^\/signup$/, crawl: "private", sample: "/signup", title: "Create an account" },
  { name: "verify", re: /^\/verify$/, crawl: "private", sample: "/verify", title: "Confirm email" },
  { name: "reset", re: /^\/reset$/, crawl: "private", sample: "/reset", title: "New password" },
  { name: "welcome", re: /^\/welcome$/, crawl: "private", sample: "/welcome", title: "Set up your login" },
  { name: "order", re: /^\/order\/([^/]+)$/, crawl: "private", sample: "/order/abc", title: "Your order" },
  /** A signed-in shopper's own orders (`/account/orders`, `/account/orders/<id>`) and the guest "track an order" form. */
  { name: "orders", re: /^\/account\/orders(?:\/([^/]+))?$/, crawl: "private", sample: "/account/orders", title: "Your orders" },
  { name: "track", re: /^\/track$/, crawl: "private", sample: "/track", title: "Track an order" },
  /** The review form behind a review-request email (product-reviews.ts); the token rides in `?token=`. */
  { name: "review", re: /^\/review$/, crawl: "private", sample: "/review", title: "Review your order" },
  /**
   * The store policies, on the SAME paths Shopify served them (`/policies/<handle>`), so years of
   * links and Google's index keep working. The text lives in `shop-policies.ts`; an unknown handle
   * is a 404 there, not here (this file stays free of the policy text the client lazy-loads).
   */
  { name: "policy", re: /^\/policies\/([^/]+)$/, crawl: "index", sample: "/policies/refund-policy" },
];

/** "/shop/p/x/" → "/p/x"; "" → "/". Collapses repeated slashes, drops a trailing one. */
export function normaliseShopPath(p: string): string {
  const s = `/${p.replace(/^\/+|\/+$/g, "")}`.replace(/\/{2,}/g, "/");
  return s;
}

/** The route a path inside the shop is, with its raw (undecoded) parameter — or null. */
export function matchShopPath(path: string): { def: ShopRouteDef; param: string | null } | null {
  const p = normaliseShopPath(path);
  for (const def of SHOP_ROUTES) {
    const m = def.re.exec(p);
    if (m) return { def, param: m[1] ?? null };
  }
  return null;
}

/** decodeURIComponent that answers null for a malformed escape instead of throwing. */
export function safeDecode(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}

/**
 * Search's query, as every cache key and handler sees it: `q` trimmed, lower-cased, ≤ 80 chars,
 * and `in` only when it is a well-formed department id. Every other parameter is dropped.
 * Matching is case-insensitive, so "Drill" and "drill " are one cache entry and one answer.
 */
export function normalisedSearchParams(sp: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams();
  const q = (sp.get("q") ?? "").trim().toLowerCase().slice(0, 80).trim();
  if (q) out.set("q", q);
  const inn = sp.get("in");
  if (inn && /^[0-9A-Za-z_-]{1,40}$/.test(inn)) out.set("in", inn);
  return out;
}

/** `/d/<id>-<slug>`: the id is everything before the first dash; it alone decides. */
export function splitDeptParam(seg: string): { id: string; slug: string } {
  const dash = seg.indexOf("-");
  return dash === -1 ? { id: seg, slug: "" } : { id: seg.slice(0, dash), slug: seg.slice(dash + 1) };
}
